import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '@mui/material';
import './ThumbsupHowItWorks.css';

/** Screen point the overlay's circular reveal grows from and collapses back to. */
export type RevealOrigin = { x: number; y: number };

/** Matches the clip-path transition in ThumbsupHowItWorks.css. */
const REVEAL_MS = 750;
const SECTION_COUNT = 5;
const SCENE_TITLES = ['The hook', 'The rule', 'The count', 'Three taps', 'Your move'];

// The scroll ride: one unit climbs the tower from START_FLOOR to TOP_FLOOR while its price falls,
// next to what a higher floor usually costs. Figures are illustrative.
const TOWER_FLOORS = 34;
const START_FLOOR = 5;
const TOP_FLOOR = 32;
const PRICE_START = 1.52;
const PRICE_TOP = 1.29;
const USUAL_TOP = 1.78;
/** Cheaper units further up the same stack; the ride stamps each one as it passes. */
const CHEAPER_FLOORS = [9, 14, 19, 24, 29];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
const clamp01 = (n: number) => Math.min(1, Math.max(0, n));
const rideOf = (floor: number) => clamp01((floor - START_FLOOR) / (TOP_FLOOR - START_FLOOR));
const priceAt = (floor: number) => PRICE_START + (PRICE_TOP - PRICE_START) * rideOf(floor);
const usualAt = (floor: number) => PRICE_START + (USUAL_TOP - PRICE_START) * rideOf(floor);
const money = (millions: number) => `$${millions.toFixed(2)}M`;
const floorLabel = (floor: number) => String(Math.round(floor)).padStart(2, '0');

/** Re-run a one-shot CSS animation by toggling its class. */
function replay(node: Element | null, className: string) {
  if (!node) return;
  node.classList.remove(className);
  void (node as HTMLElement).offsetWidth;
  node.classList.add(className);
}

/** Counts from `from` to `to` once `active` turns on; reduced motion jumps straight to `to`. */
function useCountUp(active: boolean, from: number, to: number, ms: number, delayMs = 0): number {
  const [value, setValue] = useState(from);
  useEffect(() => {
    if (!active) {
      setValue(from);
      return undefined;
    }
    if (prefersReducedMotion()) {
      setValue(to);
      return undefined;
    }
    let raf = 0;
    let start = 0;
    const timer = window.setTimeout(() => {
      const step = (now: number) => {
        if (!start) start = now;
        const t = Math.min(1, (now - start) / ms);
        setValue(from + (to - from) * easeOut(t));
        if (t < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delayMs);
    return () => {
      window.clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, [active, from, to, ms, delayMs]);
  return value;
}

function Sparkles({ count, className }: { count: number; className: string }) {
  return (
    <span className={className} aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <i
          key={i}
          style={{ '--i': i, '--x': `${6 + ((i * 41) % 88)}%`, '--y': `${10 + ((i * 29) % 78)}%` } as React.CSSProperties}
        />
      ))}
    </span>
  );
}

/** A lit tower whose highlighted unit climbs while its price tag drops. */
function MiniTower({ rows, cols, className }: { rows: number; cols: number; className: string }) {
  return (
    <span
      className={className}
      aria-hidden="true"
      style={{ '--rows': rows, '--cols': cols } as React.CSSProperties}
    >
      <span className="tuh-tower-grid">
        {Array.from({ length: rows * cols }, (_, i) => (
          <i key={i} style={{ '--row': rows - 1 - Math.floor(i / cols) } as React.CSSProperties} />
        ))}
      </span>
      <span className="tuh-lift" />
      <span className="tuh-tag tuh-tag--old">$1.52M</span>
      <span className="tuh-tag tuh-tag--new">$1.39M ↓</span>
      <span className="tuh-thumb">👍</span>
    </span>
  );
}

type BannerProps = { onOpen: (origin: RevealOrigin) => void };

/** Compact launch banner for the Thumbs Up dialog; tapping it opens the explainer overlay. */
export function ThumbsupHowItWorksBanner({ onOpen }: BannerProps) {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    // Keyboard activation reports no pointer position, so grow from the button's centre.
    const fromPointer = event.detail > 0;
    onOpen({
      x: fromPointer ? event.clientX : rect.left + rect.width / 2,
      y: fromPointer ? event.clientY : rect.top + rect.height / 2,
    });
  };

  return (
    <button type="button" className="tuh-banner" onClick={handleClick} aria-haspopup="dialog">
      <span className="tuh-blob tuh-blob--one" aria-hidden="true" />
      <span className="tuh-blob tuh-blob--two" aria-hidden="true" />
      <Sparkles count={8} className="tuh-sparkles" />
      <span className="tuh-banner-copy">
        <span className="tuh-kicker">Thumbs Up Units</span>
        <span className="tuh-banner-title">
          Higher floor. <mark>Lower price.</mark>
        </span>
        <span className="tuh-banner-sub">Find units that sit higher yet cost less, in seconds.</span>
        <span className="tuh-pill">
          See how it works <span className="tuh-pill-arrow" aria-hidden="true">→</span>
        </span>
      </span>
      <MiniTower rows={6} cols={3} className="tuh-tower tuh-tower--mini" />
    </button>
  );
}

/** The tall tower the scroll rides up: numbered floors, cheaper units to stamp, and the riding unit. */
function RideTower({
  riderRef,
  riderFloorRef,
  riderPriceRef,
}: {
  riderRef: React.RefObject<HTMLSpanElement>;
  riderFloorRef: React.RefObject<HTMLElement>;
  riderPriceRef: React.RefObject<HTMLElement>;
}) {
  return (
    <div className="tuh-ride-tower">
      <span className="tuh-ride-roof" />
      {Array.from({ length: TOWER_FLOORS }, (_, i) => {
        const floor = TOWER_FLOORS - i;
        return (
          <div key={floor} className="tuh-floor" data-floor={floor}>
            <b className="tuh-floor-no">{floorLabel(floor)}</b>
            <i />
            <i />
            <i />
            <i />
          </div>
        );
      })}
      <span className="tuh-mile tuh-mile--base" style={{ '--f': START_FLOOR } as React.CSSProperties}>
        <em>Your unit · #{floorLabel(START_FLOOR)}</em>
        <b>{money(PRICE_START)}</b>
      </span>
      {CHEAPER_FLOORS.map((floor) => (
        <span key={floor} className="tuh-mile" data-mile={floor} style={{ '--f': floor } as React.CSSProperties}>
          <em>#{floorLabel(floor)}</em>
          <b>{money(priceAt(floor))}</b>
          <i className="tuh-mile-thumb">👍</i>
          <span className="tuh-mile-burst">
            {Array.from({ length: 10 }, (_, i) => (
              <i key={i} style={{ '--a': `${i * 36}deg` } as React.CSSProperties} />
            ))}
          </span>
        </span>
      ))}
      <span ref={riderRef} className="tuh-rider">
        <span className="tuh-rider-cab" />
        <span className="tuh-rider-tag">
          <b ref={riderFloorRef}>Floor {floorLabel(START_FLOOR)}</b>
          <em ref={riderPriceRef}>{money(PRICE_START)}</em>
        </span>
      </span>
    </div>
  );
}

type OverlayProps = {
  open: boolean;
  origin: RevealOrigin | null;
  onClose: () => void;
};

/** Full-screen "how Thumbs Up works" explainer, revealed as a circle from the banner tap. */
export function ThumbsupHowItWorksOverlay({ open, origin, onClose }: OverlayProps) {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const [seen, setSeen] = useState<boolean[]>(() => Array(SECTION_COUNT).fill(false));
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      // Two frames: the overlay must paint collapsed before the circle can grow.
      let inner = 0;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setShown(true));
      });
      return () => {
        cancelAnimationFrame(outer);
        cancelAnimationFrame(inner);
      };
    }
    setShown(false);
    const timer = window.setTimeout(() => {
      setMounted(false);
      setSeen(Array(SECTION_COUNT).fill(false));
    }, prefersReducedMotion() ? 0 : REVEAL_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!scroller || typeof IntersectionObserver === 'undefined') {
      if (scroller) setSeen(Array(SECTION_COUNT).fill(true));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = sectionRefs.current.indexOf(entry.target as HTMLElement);
          if (index < 0) return;
          setSeen((prev) => {
            if (prev[index]) return prev;
            const next = [...prev];
            next[index] = true;
            return next;
          });
        });
      },
      { root: scroller, threshold: 0.3 },
    );
    sectionRefs.current.forEach((section) => {
      if (section) observer.observe(section);
    });
    return () => observer.disconnect();
  }, [scroller]);

  const setSectionRef = useCallback(
    (index: number) => (node: HTMLElement | null) => {
      sectionRefs.current[index] = node;
    },
    [],
  );

  const worldRef = useRef<HTMLDivElement>(null);
  const farRef = useRef<HTMLSpanElement>(null);
  const nearRef = useRef<HTMLSpanElement>(null);
  const cloudsRef = useRef<HTMLSpanElement>(null);
  const speedRef = useRef<HTMLSpanElement>(null);
  const riderRef = useRef<HTMLSpanElement>(null);
  const riderFloorRef = useRef<HTMLElement>(null);
  const riderPriceRef = useRef<HTMLElement>(null);
  const hudFloorRef = useRef<HTMLSpanElement>(null);
  const hudPriceRef = useRef<HTMLSpanElement>(null);
  const hudUsualRef = useRef<HTMLElement>(null);
  const hudSaveRef = useRef<HTMLElement>(null);
  const hudFoundRef = useRef<HTMLSpanElement>(null);
  const hudRef = useRef<HTMLDivElement>(null);
  const usualLineRef = useRef<SVGPathElement>(null);
  const thumbsLineRef = useRef<SVGPathElement>(null);
  const usualDotRef = useRef<SVGCircleElement>(null);
  const thumbsDotRef = useRef<SVGCircleElement>(null);
  const slateRef = useRef<HTMLSpanElement>(null);

  // Scroll is the elevator: the unit rides up the tower floor by floor, the camera follows it,
  // the city sinks and clouds pass, the price ticks down while the usual price climbs, and every
  // cheaper unit it passes gets stamped. Values are written to the DOM directly, once per frame.
  useEffect(() => {
    if (!scroller) return undefined;
    const reduce = prefersReducedMotion();
    let ride = 0;
    let lastFloor = START_FLOOR;
    let lastSection = 0;
    let lastShownFloor = -1;
    let raf = 0;
    const hit = new Set<number>();

    const sectionProgress = () => {
      const sections = sectionRefs.current.filter((s): s is HTMLElement => !!s);
      const mid = scroller.scrollTop + scroller.clientHeight / 2;
      for (let i = 0; i < sections.length - 1; i += 1) {
        const a = sections[i].offsetTop + sections[i].offsetHeight / 2;
        const b = sections[i + 1].offsetTop + sections[i + 1].offsetHeight / 2;
        if (mid < b) return i + clamp01((mid - a) / (b - a));
      }
      return Math.max(0, sections.length - 1);
    };

    const frame = () => {
      const max = Math.max(1, scroller.scrollHeight - scroller.clientHeight);
      const goal = clamp01(scroller.scrollTop / max);
      ride += (goal - ride) * (reduce ? 1 : 0.09);
      if (Math.abs(goal - ride) < 0.0002) ride = goal;

      const floor = START_FLOOR + (TOP_FLOOR - START_FLOOR) * ride;
      const velocity = floor - lastFloor;
      lastFloor = floor;
      const narrow = window.innerWidth < 860;
      const floorPx = narrow ? 46 : 60;
      const viewH = scroller.clientHeight;
      const ground = viewH * 0.12;
      // Keep the riding unit at ~45% from the bottom once it has climbed past the first screen.
      const camera = Math.max(0, ground + (floor - 1) * floorPx - viewH * 0.45);
      const shiftX = (narrow ? 14 : 22) * (1 - clamp01((ride - 0.82) / 0.18));

      const world = worldRef.current;
      if (world) {
        world.style.setProperty('--fh', `${floorPx}px`);
        world.style.transform = `translate3d(calc(-50% + ${shiftX}vw), ${camera}px, 0)`;
      }
      if (farRef.current) farRef.current.style.transform = `translate3d(0, ${camera * 0.22}px, 0)`;
      if (nearRef.current) nearRef.current.style.transform = `translate3d(0, ${camera * 0.55}px, 0)`;
      if (cloudsRef.current) cloudsRef.current.style.transform = `translate3d(0, ${camera * 1.35}px, 0)`;
      if (riderRef.current) riderRef.current.style.bottom = `${(floor - 1) * floorPx}px`;
      if (speedRef.current) speedRef.current.style.opacity = reduce ? '0' : String(clamp01(Math.abs(velocity) * 1.6));

      const price = priceAt(floor);
      const usual = usualAt(floor);
      const shownFloor = Math.round(floor);
      if (shownFloor !== lastShownFloor) {
        lastShownFloor = shownFloor;
        if (riderFloorRef.current) riderFloorRef.current.textContent = `Floor ${floorLabel(floor)}`;
        if (hudFloorRef.current) {
          hudFloorRef.current.textContent = floorLabel(floor);
          if (!reduce) replay(hudFloorRef.current, 'is-tick');
        }
        scroller.querySelectorAll('.tuh-floor.is-current').forEach((el) => el.classList.remove('is-current'));
        scroller.querySelector(`.tuh-floor[data-floor="${shownFloor}"]`)?.classList.add('is-current');
      }
      if (riderPriceRef.current) riderPriceRef.current.textContent = money(price);
      if (hudPriceRef.current) hudPriceRef.current.textContent = money(price);
      if (hudUsualRef.current) hudUsualRef.current.textContent = money(usual);
      if (hudSaveRef.current) hudSaveRef.current.textContent = `$${Math.round((usual - price) * 1000)}K`;

      const r = rideOf(floor);
      usualLineRef.current?.style.setProperty('stroke-dashoffset', String(1 - r));
      thumbsLineRef.current?.style.setProperty('stroke-dashoffset', String(1 - r));
      usualDotRef.current?.setAttribute('cx', String(6 + 148 * r));
      usualDotRef.current?.setAttribute('cy', String(44 - 34 * r));
      thumbsDotRef.current?.setAttribute('cx', String(6 + 148 * r));
      thumbsDotRef.current?.setAttribute('cy', String(44 + 14 * r));

      CHEAPER_FLOORS.forEach((mileFloor) => {
        const passed = floor >= mileFloor - 0.15;
        if (passed === hit.has(mileFloor)) return;
        const mile = scroller.querySelector(`.tuh-mile[data-mile="${mileFloor}"]`);
        if (passed) {
          hit.add(mileFloor);
          if (mile && !reduce) replay(mile, 'is-stamp');
          mile?.classList.add('is-hit');
        } else {
          hit.delete(mileFloor);
          mile?.classList.remove('is-hit', 'is-stamp');
        }
        if (hudFoundRef.current) {
          hudFoundRef.current.textContent = String(hit.size);
          if (!reduce) replay(hudFoundRef.current, 'is-tick');
        }
      });

      const sections = sectionProgress();
      scroller.style.setProperty('--dawn', clamp01((ride - 0.8) / 0.2).toFixed(4));
      // The ride is over by the finale; get the readout out of the way of its sign-off.
      hudRef.current?.classList.toggle('is-done', ride > 0.9);
      const section = Math.round(sections);
      if (section !== lastSection && Math.abs(sections - section) < 0.2) {
        lastSection = section;
        const slate = slateRef.current;
        if (slate && !reduce) {
          slate.innerHTML = `<small>Scene ${String(section + 1).padStart(2, '0')}</small><b>${SCENE_TITLES[section]}</b>`;
          replay(slate, 'is-on');
        }
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [scroller]);

  const count = useCountUp(shown && seen[2], 0, 5, 1800, 400);

  if (!mounted) return null;

  const ox = origin?.x ?? window.innerWidth / 2;
  const oy = origin?.y ?? window.innerHeight / 2;
  const sectionClass = (index: number, extra: string) =>
    `tuh-section ${extra}${seen[index] && shown ? ' is-in' : ''}`;

  return (
    <Modal open={mounted} onClose={onClose} hideBackdrop aria-label="How Thumbs Up Units works">
      <div
        ref={setScroller}
        tabIndex={-1}
        className={`tuh-overlay${shown ? ' tuh-overlay--shown' : ''}`}
        style={{ '--ox': `${ox}px`, '--oy': `${oy}px` } as React.CSSProperties}
      >
        <span className="tuh-ring" aria-hidden="true" />
        <span ref={slateRef} className="tuh-slate" aria-hidden="true" />
        <button type="button" className="tuh-close" onClick={onClose} aria-label="Close">
          ✕
        </button>

        {/* Pinned behind every section; scrolling rides the unit up the tower. */}
        <div className="tuh-backdrop" aria-hidden="true">
          <span className="tuh-sky" />
          <span className="tuh-dawn">
            <span className="tuh-sun" />
          </span>
          <Sparkles count={26} className="tuh-stars" />
          <span ref={farRef} className="tuh-skyline tuh-skyline--far" />
          <span ref={nearRef} className="tuh-skyline tuh-skyline--near" />
          <div ref={worldRef} className="tuh-world">
            <RideTower riderRef={riderRef} riderFloorRef={riderFloorRef} riderPriceRef={riderPriceRef} />
          </div>
          <span ref={cloudsRef} className="tuh-clouds">
            <i style={{ '--cx': '8%', '--cy': '120%' } as React.CSSProperties} />
            <i style={{ '--cx': '62%', '--cy': '170%' } as React.CSSProperties} />
            <i style={{ '--cx': '24%', '--cy': '230%' } as React.CSSProperties} />
            <i style={{ '--cx': '74%', '--cy': '290%' } as React.CSSProperties} />
            <i style={{ '--cx': '40%', '--cy': '350%' } as React.CSSProperties} />
          </span>
          <span ref={speedRef} className="tuh-speed" />
          <span className="tuh-veil" />
        </div>

        {/* Live readout of the ride. */}
        <div ref={hudRef} className={`tuh-hud${shown ? ' is-on' : ''}`} aria-hidden="true">
          <span className="tuh-hud-cell">
            <em>Floor</em>
            <b className="tuh-hud-floor">
              <span ref={hudFloorRef}>{floorLabel(START_FLOOR)}</span>
              <i>↑</i>
            </b>
          </span>
          <span className="tuh-hud-cell">
            <em>Thumbs Up price</em>
            <b className="tuh-hud-price">
              <span ref={hudPriceRef}>{money(PRICE_START)}</span>
              <i>↓</i>
            </b>
          </span>
          <span className="tuh-hud-cell tuh-hud-cell--usual">
            <em>Usual higher-floor price</em>
            <s ref={hudUsualRef}>{money(PRICE_START)}</s>
          </span>
          <span className="tuh-hud-cell tuh-hud-cell--save">
            <em>Buyer saves</em>
            <b ref={hudSaveRef}>$0K</b>
          </span>
          <svg className="tuh-hud-chart" viewBox="0 0 160 64">
            <path className="tuh-hud-grid" d="M6 44 H154" />
            <path ref={usualLineRef} className="tuh-hud-usual" d="M6 44 L154 10" pathLength={1} />
            <path ref={thumbsLineRef} className="tuh-hud-thumbs" d="M6 44 L154 58" pathLength={1} />
            <circle ref={usualDotRef} className="tuh-hud-dot tuh-hud-dot--usual" cx="6" cy="44" r="3.5" />
            <circle ref={thumbsDotRef} className="tuh-hud-dot tuh-hud-dot--thumbs" cx="6" cy="44" r="4" />
          </svg>
          <span className="tuh-hud-cell tuh-hud-cell--found">
            <em>Cheaper above</em>
            <b>
              👍 <span ref={hudFoundRef}>0</span>
            </b>
          </span>
        </div>

        {/* 1. Hook */}
        <section ref={setSectionRef(0)} className={sectionClass(0, 'tuh-hero')}>
          <div className="tuh-hero-copy">
            <p className="tuh-eyebrow">Super Picker · Thumbs Up Units</p>
            <h1 className="tuh-h1">
              <span className="tuh-h1-line">Higher floor.</span>
              <span className="tuh-h1-line">
                <mark>Lower price.</mark>
              </span>
            </h1>
            <p className="tuh-lead">Really. Scroll to ride up and watch the price drop.</p>
          </div>
          <span className="tuh-scroll-cue" aria-hidden="true">
            Scroll to ride up <b>↓</b>
          </span>
        </section>

        {/* 2. The rule */}
        <section ref={setSectionRef(1)} className={sectionClass(1, 'tuh-rule')}>
          <div className="tuh-wrap">
            <p className="tuh-eyebrow">How a unit earns it</p>
            <h2 className="tuh-h2">
              Three checks. <mark>One thumbs up.</mark>
            </h2>
            <p className="tuh-body">Compared with the unit you are looking at, a Thumbs Up unit is:</p>
            <div className="tuh-cards">
              <div className="tuh-card" style={{ '--d': 0 } as React.CSSProperties}>
                <span className="tuh-card-icon">🏢</span>
                <b>Same project, same bedroom type</b>
                <span>A true like-for-like comparison.</span>
              </div>
              <div className="tuh-card tuh-card--up" style={{ '--d': 1 } as React.CSSProperties}>
                <span className="tuh-card-icon">↑</span>
                <b>On a higher floor</b>
                <span>Better views, more light.</span>
              </div>
              <div className="tuh-card tuh-card--down" style={{ '--d': 2 } as React.CSSProperties}>
                <span className="tuh-card-icon">↓</span>
                <b>Lower price and lower PSF</b>
                <span>Your buyer pays less for more.</span>
              </div>
            </div>
            <div className="tuh-verdict">
              <span className="tuh-verdict-thumb">👍</span>
              Thumbs Up unit
            </div>
          </div>
        </section>

        {/* 3. Reading the count */}
        <section ref={setSectionRef(2)} className={sectionClass(2, 'tuh-count')}>
          <div className="tuh-wrap tuh-split">
            <div>
              <p className="tuh-eyebrow">Reading the list</p>
              <h2 className="tuh-h2">
                The number is your <mark>head start.</mark>
              </h2>
              <p className="tuh-body">
                Each unit in the list shows a green thumbs-up count. It tells you how many available units on
                higher floors cost less than it does.
              </p>
              <div className="tuh-unit-card">
                <span>
                  <b>Unit #05-12</b>
                  <em>3BR · 1,055 sqft</em>
                </span>
                <span className="tuh-chip">👍 {Math.round(count)}</span>
              </div>
              <p className="tuh-caption">{Math.round(count)} higher-floor units cost less than this one.</p>
            </div>
            <div className="tuh-stack" aria-hidden="true">
              {Array.from({ length: 6 }, (_, i) => {
                const level = 5 - i;
                return (
                  <span
                    key={level}
                    className={level === 0 ? 'tuh-stack-unit tuh-stack-unit--base' : 'tuh-stack-unit'}
                    style={{ '--d': level } as React.CSSProperties}
                  >
                    {level === 0 ? 'This unit' : `Floor +${level} · cheaper`}
                  </span>
                );
              })}
            </div>
          </div>
        </section>

        {/* 4. Use it */}
        <section ref={setSectionRef(3)} className={sectionClass(3, 'tuh-steps')}>
          <div className="tuh-wrap">
            <p className="tuh-eyebrow">Use it in 3 taps</p>
            <h2 className="tuh-h2">
              From list to <mark>best-value unit.</mark>
            </h2>
            <div className="tuh-step-grid">
              <div className="tuh-step" style={{ '--d': 0 } as React.CSSProperties}>
                <span className="tuh-step-no">1</span>
                <div className="tuh-mock tuh-mock--list" aria-hidden="true">
                  <span>Unit #03-08 <i>👍 3</i></span>
                  <span className="is-tapped">Unit #05-12 <i>👍 5</i></span>
                  <span>Unit #07-02 <i>👍 2</i></span>
                  <b className="tuh-tap" />
                </div>
                <b>Pick a unit</b>
                <span>Tap any unit in this list to open it in Site Plans.</span>
              </div>
              <div className="tuh-step" style={{ '--d': 1 } as React.CSSProperties}>
                <span className="tuh-step-no">2</span>
                <div className="tuh-mock tuh-mock--scroll" aria-hidden="true">
                  <span className="tuh-mock-page">
                    <i />
                    <i />
                    <i />
                    <strong>Budget Matched Units (Cross Project)</strong>
                    <i />
                  </span>
                </div>
                <b>Scroll to Budget Matched Units (Cross Project)</b>
                <span>Every comparable unit for that budget is listed there.</span>
              </div>
              <div className="tuh-step" style={{ '--d': 2 } as React.CSSProperties}>
                <span className="tuh-step-no">3</span>
                <div className="tuh-mock tuh-mock--sort" aria-hidden="true">
                  <span className="tuh-mock-bar">
                    <i className="tuh-mock-thumb">👍</i>
                    <em>Price ↑</em>
                  </span>
                  <span className="tuh-mock-rows">
                    <span style={{ '--from': 0, '--to': 2 } as React.CSSProperties}>#18-12 · $1.48M</span>
                    <span style={{ '--from': 1, '--to': 0 } as React.CSSProperties}>#21-12 · $1.39M</span>
                    <span style={{ '--from': 2, '--to': 1 } as React.CSSProperties}>#16-12 · $1.42M</span>
                  </span>
                </div>
                <b>Tap 👍 and sort by price</b>
                <span>Only the Thumbs Up units stay, cheapest first.</span>
              </div>
            </div>
          </div>
        </section>

        {/* 5. CTA */}
        <section ref={setSectionRef(4)} className={sectionClass(4, 'tuh-final')}>
          <Sparkles count={14} className="tuh-sparkles tuh-sparkles--final" />
          <div className="tuh-wrap tuh-final-copy">
            <span className="tuh-final-thumb" aria-hidden="true">👍</span>
            <h2 className="tuh-h2">
              Your buyer wants the higher floor. <mark>Find it for less.</mark>
            </h2>
            <button type="button" className="tuh-cta-btn" onClick={onClose}>
              Find my first Thumbs Up unit
            </button>
            <div className="tuh-sig">
              <img
                src="/campaigns/sharable-links/jy-linus.png"
                alt="Linus Low and Ngiam Juyong"
                width={260}
                height={242}
              />
              <div className="tuh-sig-names">
                <div>
                  <b>Linus Low</b>
                  <small>
                    Chief Transformation
                    <br />
                    Officer (CTrO)
                  </small>
                </div>
                <div>
                  <b>Ngiam Juyong</b>
                  <small>Project Director</small>
                </div>
              </div>
              <div className="tuh-sig-office">Projects &amp; Transformation Office</div>
            </div>
            <p className="tuh-footnote">Floors, prices and counts shown here are examples.</p>
          </div>
        </section>
      </div>
    </Modal>
  );
}
